'use client'

import { useState, useCallback } from 'react'
import ReactMarkdown from 'react-markdown'
import Link from 'next/link'
import { Copy, Check } from 'lucide-react'
import { cn } from '@/lib/utils'

/**
 * Variables available for interpolation in system messages.
 * These are contextual based on where the message is displayed.
 */
export interface MessageVariables {
  // User variables (available when authenticated)
  userName?: string        // Full name
  userFirstName?: string   // First name only
  userEmail?: string       // Email address
  
  // Artist variables (available in artist dashboard context)
  artistName?: string      // Artist/band name
  artistHandle?: string    // Artist handle (URL slug)
}

interface MessageMarkdownProps {
  children: string
  /**
   * Artist handle for constructing dashboard links.
   * Required when using [[dashboard/...]] link syntax.
   */
  artistHandle?: string
  /**
   * Variables for interpolation using {{variableName}} syntax.
   * Available variables depend on the context (e.g., dashboard has user/artist info).
   */
  variables?: MessageVariables
  className?: string
}

/**
 * Inline copyable text component - renders as a styled code with copy button
 */
function CopyableText({ text }: { text: string }) {
  const [copied, setCopied] = useState(false)

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(text)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (err) {
      console.error('Failed to copy text:', err)
    }
  }, [text])

  return (
    <button
      onClick={handleCopy}
      className={cn(
        'inline-flex items-center gap-1.5 px-2 py-0.5 rounded',
        'bg-violet-500/20 border border-violet-500/30',
        'text-violet-300 font-mono text-[0.9em] font-medium',
        'hover:bg-violet-500/30 hover:border-violet-500/40',
        'transition-all cursor-pointer',
        copied && 'bg-emerald-500/20 border-emerald-500/30 text-emerald-300'
      )}
      title={copied ? 'Copied!' : 'Click to copy'}
    >
      <span>{text}</span>
      {copied ? (
        <Check className="h-3 w-3" />
      ) : (
        <Copy className="h-3 w-3 opacity-60" />
      )}
    </button>
  )
}

/**
 * Custom syntax for system messages:
 * 
 * Links:
 * - [[/path]] - Absolute path (e.g., [[/discover]])
 * - [[dashboard]] - Artist dashboard home (requires artistHandle)
 * - [[dashboard/pieces]] - Artist dashboard section (requires artistHandle)
 * - [[dashboard/pieces?tab=setlists]] - With query params
 * - Standard markdown links also work: [text](url)
 * 
 * Copyable text:
 * - [[copy:CODE]] - Renders as inline copyable text (e.g., promo codes)
 * 
 * Variable interpolation:
 * - {{userName}} - User's full name
 * - {{userFirstName}} - User's first name
 * - {{userEmail}} - User's email
 * - {{artistName}} - Artist/band name
 * - {{artistHandle}} - Artist handle
 */
export function MessageMarkdown({ children, artistHandle, variables, className }: MessageMarkdownProps) {
  // First, interpolate variables
  const interpolatedContent = interpolateVariables(children, variables)
  
  // Split content into segments: regular markdown and copyable text
  const segments = splitIntoCopyableSegments(interpolatedContent)

  return (
    <span className={cn('inline', className)}>
      {segments.map((segment, index) => {
        if (segment.type === 'copyable') {
          // Preserve leading/trailing whitespace from the segment boundaries
          const { leadingSpace, trailingSpace, content } = segment
          return (
            <span key={`segment-${index}`}>
              {leadingSpace && ' '}
              <CopyableText text={content} />
              {trailingSpace && ' '}
            </span>
          )
        }

        // Regular markdown segment - process links
        const processedContent = processCustomLinks(segment.content, artistHandle)

        return (
          <ReactMarkdown
            key={`segment-${index}`}
            components={{
              // Override link rendering to use Next.js Link for internal URLs
              a: ({ href, children }) => {
                if (!href) return <>{children}</>

                // Internal links (starting with /)
                if (href.startsWith('/')) {
                  return (
                    <Link
                      href={href}
                      className="text-inherit underline underline-offset-2 hover:opacity-80 transition-opacity"
                    >
                      {children}
                    </Link>
                  )
                }

                // External links
                return (
                  <a
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-inherit underline underline-offset-2 hover:opacity-80 transition-opacity"
                  >
                    {children}
                  </a>
                )
              },
              // Style overrides for inline elements - render as spans to stay inline
              p: ({ children }) => <span>{children}</span>,
              strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
              em: ({ children }) => <em>{children}</em>,
              code: ({ children }) => (
                <code className="px-1 py-0.5 rounded bg-white/10 text-[0.9em] font-mono">
                  {children}
                </code>
              ),
            }}
          >
            {processedContent}
          </ReactMarkdown>
        )
      })}
    </span>
  )
}

type Segment = { 
  type: 'text' | 'copyable'
  content: string
  // For copyable segments, track if there was whitespace before/after in the original content
  leadingSpace?: boolean
  trailingSpace?: boolean
}

/**
 * Split content into segments of regular text and copyable text ([[copy:...]])
 */
function splitIntoCopyableSegments(content: string): Segment[] {
  const segments: Segment[] = []
  // Match [[copy:TEXT]] syntax - the copy: prefix distinguishes from regular links
  // Also capture any whitespace immediately before and after
  const pattern = /(\s?)\[\[copy:([^\]]+)\]\](\s?)/g
  let lastIndex = 0
  let match

  while ((match = pattern.exec(content)) !== null) {
    const [fullMatch, leadingWs, copyText, trailingWs] = match
    const matchStart = match.index
    
    // Add text before the match (excluding any leading whitespace we captured)
    const _textEnd = matchStart + (leadingWs ? leadingWs.length : 0)
    if (matchStart > lastIndex) {
      segments.push({
        type: 'text',
        content: content.slice(lastIndex, matchStart),
      })
    }

    // Add the copyable segment with whitespace flags
    segments.push({
      type: 'copyable',
      content: copyText.trim(),
      leadingSpace: !!leadingWs,
      trailingSpace: !!trailingWs,
    })

    lastIndex = matchStart + fullMatch.length
  }

  // Add remaining text
  if (lastIndex < content.length) {
    segments.push({
      type: 'text',
      content: content.slice(lastIndex),
    })
  }

  return segments.length > 0 ? segments : [{ type: 'text', content }]
}

/**
 * Interpolate {{variable}} syntax with actual values
 */
function interpolateVariables(content: string, variables?: MessageVariables): string {
  if (!variables) return content
  
  // Match {{variableName}} syntax
  return content.replace(/\{\{(\w+)\}\}/g, (match, varName: string) => {
    // Map variable names to values
    const varMap: Record<string, string | undefined> = {
      userName: variables.userName,
      userFirstName: variables.userFirstName,
      userEmail: variables.userEmail,
      artistName: variables.artistName,
      artistHandle: variables.artistHandle,
    }
    
    const value = varMap[varName]
    
    // If variable not found or undefined, leave the placeholder as-is
    // This makes it obvious in the UI that a variable wasn't resolved
    if (value === undefined) {
      return match
    }
    
    return value
  })
}

/**
 * Process custom [[link]] syntax into standard markdown links
 */
function processCustomLinks(content: string, artistHandle?: string): string {
  // Match [[path]] or [[path|label]] syntax
  const linkPattern = /\[\[([^\]|]+)(?:\|([^\]]+))?\]\]/g
  
  return content.replace(linkPattern, (_, path: string, label?: string) => {
    const resolvedPath = resolvePath(path.trim(), artistHandle)
    const linkLabel = label?.trim() || getDefaultLabel(path.trim())
    return `[${linkLabel}](${resolvedPath})`
  })
}

/**
 * Resolve a custom path to an actual URL
 */
function resolvePath(path: string, artistHandle?: string): string {
  // Absolute paths - use as-is
  if (path.startsWith('/')) {
    return path
  }
  
  // Dashboard paths - prefix with artist handle
  if (path.startsWith('dashboard')) {
    if (!artistHandle) {
      console.warn(`MessageMarkdown: artistHandle required for dashboard link: ${path}`)
      return '#'
    }
    return `/${artistHandle}/${path}`
  }
  
  // Known shortcuts
  const shortcuts: Record<string, string> = {
    'home': '/',
    'discover': '/discover',
    'login': '/login',
    'signup': '/signup',
    'terms': '/terms',
    'privacy': '/privacy',
    'contact': '/contact',
  }
  
  if (shortcuts[path]) {
    return shortcuts[path]
  }
  
  // Unknown path - treat as relative to root
  return `/${path}`
}

/**
 * Generate a default label for common paths
 */
function getDefaultLabel(path: string): string {
  const labels: Record<string, string> = {
    'home': 'Home',
    'discover': 'Discover',
    'login': 'Log in',
    'signup': 'Sign up',
    'terms': 'Terms of Service',
    'privacy': 'Privacy Policy',
    'contact': 'Contact',
    'dashboard': 'Dashboard',
    'dashboard/pieces': 'Repertoire',
    'dashboard/events': 'Events',
    'dashboard/analytics': 'Analytics',
    'dashboard/settings': 'Settings',
    'dashboard/people': 'People',
    'dashboard/promotions': 'Promotions',
    'dashboard/live': 'Live Console',
  }
  
  // Check for exact match
  if (labels[path]) {
    return labels[path]
  }
  
  // For paths like /dashboard/pieces?tab=setlists, extract the base
  const basePath = path.split('?')[0]
  if (labels[basePath]) {
    return labels[basePath]
  }
  
  // Fallback: capitalize the last segment
  const segments = basePath.split('/')
  const lastSegment = segments[segments.length - 1]
  return lastSegment.charAt(0).toUpperCase() + lastSegment.slice(1)
}

